import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
  Chip,
  IconButton,
  Autocomplete,
  FormHelperText
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  AttachMoney as AttachMoneyIcon,
  CalendarToday as CalendarIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Receipt as ReceiptIcon,
  School as SchoolIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useNavigate, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { additionalPaymentService } from '../../services/additionalPaymentService';
import { studentService, parentService } from '../../services/api';
import { uploadService } from '../../services/uploadService';
import dayjs from 'dayjs';
import DatePickerField from '../../components/DatePickerField';

const paymentMethods = [
  { value: 'cash', label: 'Efectivo' },
  { value: 'bank_transfer', label: 'Transferencia Bancaria' },
  { value: 'card', label: 'Tarjeta' },
  { value: 'other', label: 'Otro' }
];

const currencies = [
  { value: 'RUB', label: 'Rublo Ruso', symbol: '₽' },
  { value: 'USD', label: 'Dólar Americano', symbol: '$' },
  { value: 'EUR', label: 'Euro', symbol: '€' }
];

const schema = yup.object({
  student_id: yup.number()
    .required('Debe seleccionar un estudiante')
    .typeError('Debe seleccionar un estudiante válido'),
  parent_id: yup.number(),
  concept: yup.string().required('Debe seleccionar un concepto'),
  description: yup.string().nullable(),
  amount: yup.number()
    .required('El monto es requerido')
    .min(0.01, 'Monto debe ser mayor a 0')
    .typeError('Debe ser un número válido'),
  payment_date: yup.string()
    .required('La fecha es requerida')
    .test('not-future', 'La fecha de pago no puede ser futura', (value) => {
      return dayjs(value).isSameOrBefore(dayjs(), 'day');
    }),
  currency: yup.string().required('La moneda es requerida'),
  payment_method: yup.string().nullable(),
  reference_number: yup.string().nullable(),
  status: yup.string().required('El estado es requerido'),
  comments: yup.string().nullable(),
  receipt_url: yup.string().nullable()
});

const AdditionalPaymentForm: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  
  const [loading, setLoading] = useState(false);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [loadingConcepts, setLoadingConcepts] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  
  // Estados para datos
  const [students, setStudents] = useState<any[]>([]);
  const [parents, setParents] = useState<any[]>([]);
  const [concepts, setConcepts] = useState<Record<string, string>>({});
  const [selectedStudent, setSelectedStudent] = useState<any>(null);
  const [selectedParent, setSelectedParent] = useState<any>(null);
  const [loadingParents, setLoadingParents] = useState(false);
  
  // Estados para archivo
  const [paymentFile, setPaymentFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [uploadedFile, setUploadedFile] = useState<any>(null);
  const [uploadingFile, setUploadingFile] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      student_id: undefined,
      parent_id: undefined,
      concept: '',
      description: '',
      amount: 0,
      payment_date: dayjs().format('YYYY-MM-DD'),
      currency: 'RUB', 
      payment_method: 'bank_transfer',
      reference_number: '',
      status: 'paid',
      comments: '',
      receipt_url: ''
    }
  });

  const selectedStudentId = watch('student_id');
  const selectedParentId = watch('parent_id');

  useEffect(() => {
    loadStudents();
    loadParents();
    loadConcepts();
  }, []);

  useEffect(() => {
    if (selectedStudentId) {
      loadStudentDetails(selectedStudentId);
    } else {
      setSelectedStudent(null);
    }
  }, [selectedStudentId]);

  const loadStudents = async () => {
    try {
      setLoadingStudents(true);
      const response = await studentService.getAll({ per_page: 500, status: 'active' });
      setStudents(response.data.data || []);
    } catch (err) {
      console.error('Error loading students:', err);
    } finally {
      setLoadingStudents(false);
    }
  };

  const loadParents = async () => {
    try {
        setLoadingParents(true);
        const response = await parentService.getAll({ per_page: 500, active: true });
        setParents(response.data.data || []);
    } catch (err) {
        console.error('Error loading parents:', err);
    } finally {
        setLoadingParents(false);
    }
  };

  const loadConcepts = async () => {
    try {
      setLoadingConcepts(true);
      const response = await additionalPaymentService.getConcepts();
      setConcepts(response.data);
    } catch (err) {
      console.error('Error loading concepts:', err);
      // Valores por defecto en caso de error
      setConcepts({
        'Matrícula': 'Pago de matrícula anual',
        'Materiales': 'Materiales educativos',
        'Excursión': 'Actividades extracurriculares',
        'Uniforme': 'Uniforme escolar',
        'Examen': 'Exámenes internacionales',
        'Biblioteca': 'Uso de biblioteca',
        'Tecnología': 'Equipos tecnológicos',
        'Deporte': 'Actividades deportivas',
        'Otro': 'Otros conceptos'
      });
    } finally {
      setLoadingConcepts(false);
    }
  };

  const loadStudentDetails = async (studentId: number) => {
    try {
      const response = await studentService.getById(studentId);
      setSelectedStudent(response.data);
    } catch (err) {
      console.error('Error loading student details:', err);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];
      if (!validTypes.includes(file.type)) {
        setError('Solo se permiten imágenes (JPEG, PNG, GIF) y archivos PDF');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError('El archivo no puede ser mayor a 5MB');
        return;
      }
      setPaymentFile(file);
      setError(null);
      
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setFilePreview(e.target?.result as string);
        };
        reader.readAsDataURL(file);
      } else {
        setFilePreview(null);
      }
    }
  };

  const handleFileUpload = async (file: File): Promise<string> => {
    try {
      setUploadingFile(true);
      const response = await uploadService.uploadPaymentProof(file);
      const fileUrl = response.data.file_url;
      
      setUploadedFile({
        name: file.name,
        url: response.data.file_url,
        type: file.type
      });

      setValue('receipt_url', fileUrl);

      return fileUrl;
    } catch (err: any) {
      throw new Error('Error al subir archivo');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
    setPaymentFile(null);
    setFilePreview(null);
    setValue('receipt_url', '');
  };

  const onSubmit = async (data: any) => {
    try {
      setLoading(true);
      setError(null);

      let receiptUrl = data.receipt_url;
      if (paymentFile && !uploadedFile) {
        receiptUrl = await handleFileUpload(paymentFile);
      }

      const paymentData = {
        ...data,
        receipt_url: receiptUrl
      };

      await additionalPaymentService.create(paymentData);

      setSuccess(true);
      setTimeout(() => navigate('/additional-payments'), 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al guardar el pago');
    } finally {
      setLoading(false);
    }
  };

  // Opciones para el Autocomplete de estudiantes
  const studentOptions = students.map(student => ({
    id: student.id,
    label: `${student.first_name} ${student.last_name} - ${student.grade}${student.sponsor ? ` (${student.sponsor})` : ''}`,
    student
  }));

  const parentOptions = parents.map(parent => ({
    id: parent.id,
    label: parent.spanish_name || parent.russian_name,
    subtitle: `${parent.email || ''} ${parent.phone ? `| ${parent.country_code} ${parent.phone}` : ''}`,
    parent
  }));

  const conceptOptions = Object.entries(concepts).map(([key, value]) => ({
    value: key,
    label: key,
    description: value
  }));

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton component={Link} to="/additional-payments" sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1">
          Nuevo Pago Adicional
        </Typography>
      </Box>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          ¡Pago adicional registrado exitosamente! Redirigiendo...
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={3}>
          {/* Selección de Estudiante con Autocomplete */}
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  1. Seleccionar Estudiante
                </Typography>
                
                <Controller
                  name="student_id"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      options={studentOptions}
                      getOptionLabel={(option) => option.label}
                      isOptionEqualToValue={(option, value) => option.id === value?.id}
                      value={studentOptions.find(opt => opt.id === field.value) || null}
                      onChange={(_, newValue) => {
                        field.onChange(newValue?.id || null);
                      }}
                      loading={loadingStudents}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Buscar Estudiante *"
                          error={!!errors.student_id}
                          helperText={errors.student_id?.message}
                        />
                      )}
                      filterOptions={(options, state) => {
                        const inputValue = state.inputValue.toLowerCase().trim();
                        if (!inputValue) return options;
                        return options.filter(option =>
                          option.label.toLowerCase().includes(inputValue)
                        );
                      }}
                      renderOption={(props, option) => {
                        const { key, ...otherProps } = props;
                        return (
                          <li key={key} {...otherProps}>
                            <Box display="flex" flexDirection="column">
                              <Typography variant="body1">
                                {option.student.first_name} {option.student.last_name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Grado: {option.student.grade} {option.student.sponsor ? ` | Patrocinador: ${option.student.sponsor}` : ''}
                              </Typography>
                            </Box>
                          </li>
                        );
                      }}
                      noOptionsText="No hay estudiantes disponibles"
                      loadingText="Cargando estudiantes..."
                    />
                  )}
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Información del Estudiante Seleccionado */}
          {selectedStudent && (
            <Grid size={{ xs: 12 }}>
              <Alert severity="info" sx={{ bgcolor: 'primary.50' }}>
                <Typography variant="subtitle2" color="primary.main">
                  Estudiante seleccionado:
                </Typography>
                <Typography variant="body1">
                  <strong>{selectedStudent.first_name} {selectedStudent.last_name}</strong> - Grado: {selectedStudent.grade}
                  {selectedStudent.sponsor && ` - Patrocinador: ${selectedStudent.sponsor}`}
                </Typography>
              </Alert>
            </Grid>
          )}

        <Grid size={{ xs: 12 }}>
            <Card>
                <CardContent>
                <Typography variant="h6" gutterBottom>
                    2. Persona que realiza el pago
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                    Si el pago lo realiza una persona diferente al padre/tutor asignado al estudiante, puedes seleccionarla aquí. 
                    Deja vacío para usar el padre asignado al estudiante.
                </Typography>
                
                <Controller
                    name="parent_id"
                    control={control}
                    render={({ field }) => (
                    <Autocomplete
                        options={parentOptions}
                        getOptionLabel={(option) => option.label}
                        isOptionEqualToValue={(option, value) => option.id === value?.id}
                        value={parentOptions.find(opt => opt.id === field.value) || null}
                        onChange={(_, newValue) => {
                        field.onChange(newValue?.id || null);
                        setSelectedParent(newValue?.parent || null);
                        }}
                        loading={loadingParents}
                        renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Buscar Padre/Tutor (opcional)"
                            placeholder="Escribe para buscar por nombre, email o teléfono..."
                            helperText="Deja vacío si no aplica o es el padre asignado"
                        />
                        )}
                        filterOptions={(options, state) => {
                        const inputValue = state.inputValue.toLowerCase().trim();
                        if (!inputValue) return options;
                        return options.filter(option =>
                            option.label.toLowerCase().includes(inputValue) ||
                            option.subtitle.toLowerCase().includes(inputValue)
                        );
                        }}
                        renderOption={(props, option) => {
                        const { key, ...otherProps } = props;
                        return (
                            <li key={key} {...otherProps}>
                            <Box display="flex" flexDirection="column">
                                <Typography variant="body1">
                                {option.label}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                {option.subtitle || 'Sin información de contacto'}
                                </Typography>
                            </Box>
                            </li>
                        );
                        }}
                        noOptionsText="No hay padres registrados"
                        loadingText="Cargando padres..."
                    />
                    )}
                />
                
                {selectedParent && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" color="primary.main">
                        Persona que realizará el pago:
                    </Typography>
                    <Typography variant="body2">
                        <strong>{selectedParent.spanish_name || selectedParent.russian_name}</strong>
                        {selectedParent.email && <span> - {selectedParent.email}</span>}
                        {selectedParent.phone && <span> - {selectedParent.country_code} {selectedParent.phone}</span>}
                    </Typography>
                    </Alert>
                )}
                
                {!selectedParent && selectedStudent?.parent && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                        Por defecto, se usará el padre asignado al estudiante: 
                        <strong> {selectedStudent.parent?.spanish_name || selectedStudent.parent?.russian_name}</strong>
                    </Typography>
                    </Alert>
                )}
                </CardContent>
            </Card>
        </Grid>

          {/* Datos del Pago */}
          <Grid size={{ xs: 12 }}>
            <Card>
                <CardContent>
                <Typography variant="h6" gutterBottom>
                    3. Datos del Pago
                </Typography>

                <Grid container spacing={2}>
                    {/* Concepto */}
                    <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                        name="concept"
                        control={control}
                        render={({ field }) => (
                        <Autocomplete
                            options={conceptOptions}
                            getOptionLabel={(option) => option.label}
                            isOptionEqualToValue={(option, value) => option.value === value?.value}
                            value={conceptOptions.find(opt => opt.value === field.value) || null}
                            onChange={(_, newValue) => {
                            field.onChange(newValue?.value || '');
                            if (newValue?.description) {
                                setValue('description', newValue.description);
                            }
                            }}
                            loading={loadingConcepts}
                            renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Concepto *"
                                error={!!errors.concept}
                                helperText={errors.concept?.message}
                            />
                            )}
                            renderOption={(props, option) => {
                            const { key, ...otherProps } = props;
                            return (
                                <li key={key} {...otherProps}>
                                <Box display="flex" flexDirection="column">
                                    <Typography variant="body1">
                                    {option.label}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                    {option.description}
                                    </Typography>
                                </Box>
                                </li>
                            );
                            }}
                            noOptionsText="No hay conceptos disponibles"
                        />
                        )}
                    />
                    </Grid>

                    {/* Monto */}
                    <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                        name="amount"
                        control={control}
                        render={({ field }) => (
                        <TextField
                            {...field}
                            fullWidth
                            type="number"
                            label="Monto *"
                            error={!!errors.amount}
                            helperText={errors.amount?.message}
                            InputProps={{
                            startAdornment: <InputAdornment position="start">₽</InputAdornment>
                            }}
                            onWheel={(e) => (e.target as HTMLInputElement).blur()}
                        />
                        )}
                    />
                    </Grid>

                    {/* Fecha de Pago */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Controller
                        name="payment_date"
                        control={control}
                        render={({ field }) => (
                          <DatePickerField
                            {...field}
                            label="Fecha de Ingreso"
                            error={!!errors.payment_date}
                            helperText={errors.payment_date?.message}
                            disabled={loading}
                          />
                        )}
                      />
                    </Grid>
                    {/*<Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                        name="payment_date"
                        control={control}
                        render={({ field }) => (
                        <TextField
                            {...field}
                            fullWidth
                            type="date"
                            label="Fecha de Pago *"
                            InputLabelProps={{ shrink: true }}
                            error={!!errors.payment_date}
                            helperText={errors.payment_date?.message}
                            InputProps={{
                            startAdornment: <InputAdornment position="start"><CalendarIcon /></InputAdornment>
                            }}
                        />
                        )}
                    />
                    </Grid>*/}

                    {/* 👈 NUEVO CAMPO: Moneda */}
                    <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                        name="currency"
                        control={control}
                        render={({ field }) => (
                        <FormControl fullWidth error={!!errors.currency}>
                            <InputLabel>Moneda *</InputLabel>
                            <Select {...field} label="Moneda *">
                            {currencies.map((c) => (
                                <MenuItem key={c.value} value={c.value}>
                                <Box display="flex" alignItems="center">
                                    <span style={{ marginRight: 8 }}>{c.symbol}</span>
                                    {c.label}
                                </Box>
                                </MenuItem>
                            ))}
                            </Select>
                            {errors.currency && (
                            <FormHelperText>{errors.currency.message}</FormHelperText>
                            )}
                        </FormControl>
                        )}
                    />
                    </Grid>

                    {/* Método de Pago */}
                    <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                        name="payment_method"
                        control={control}
                        render={({ field }) => (
                        <FormControl fullWidth>
                            <InputLabel>Método de Pago</InputLabel>
                            <Select {...field} label="Método de Pago">
                            {paymentMethods.map((m) => (
                                <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                            ))}
                            </Select>
                        </FormControl>
                        )}
                    />
                    </Grid>

                    {/* Número de Referencia */}
                    <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                        name="reference_number"
                        control={control}
                        render={({ field }) => (
                        <TextField
                            {...field}
                            fullWidth
                            label="Número de Referencia"
                            placeholder="Transferencia, comprobante, etc."
                        />
                        )}
                    />
                    </Grid>

                    {/* Estado */}
                    <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                        name="status"
                        control={control}
                        render={({ field }) => (
                        <FormControl fullWidth error={!!errors.status}>
                            <InputLabel>Estado *</InputLabel>
                            <Select {...field} label="Estado *">
                            <MenuItem value="pending">Pendiente</MenuItem>
                            <MenuItem value="paid">Pagado</MenuItem>
                            <MenuItem value="cancelled">Cancelado</MenuItem>
                            </Select>
                            {errors.status && (
                            <FormHelperText>{errors.status.message}</FormHelperText>
                            )}
                        </FormControl>
                        )}
                    />
                    </Grid>

                    {/* Descripción */}
                    <Grid size={{ xs: 12 }}>
                    <Controller
                        name="description"
                        control={control}
                        render={({ field }) => (
                        <TextField
                            {...field}
                            fullWidth
                            multiline
                            rows={2}
                            label="Descripción"
                            placeholder="Detalles adicionales del concepto..."
                        />
                        )}
                    />
                    </Grid>

                    {/* Comentarios */}
                    <Grid size={{ xs: 12 }}>
                    <Controller
                        name="comments"
                        control={control}
                        render={({ field }) => (
                        <TextField
                            {...field}
                            fullWidth
                            multiline
                            rows={2}
                            label="Comentarios"
                            placeholder="Información adicional sobre el pago..."
                        />
                        )}
                    />
                    </Grid>
                </Grid>
                </CardContent>
            </Card>
        </Grid>

          {/* Comprobante de Pago */}
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  4. Comprobante de Pago
                </Typography>

                <input
                  type="file"
                  id="receipt-upload"
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                  accept=".jpg,.jpeg,.png,.gif,.pdf"
                />

                {uploadedFile ? (
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Box display="flex" alignItems="center">
                      <ReceiptIcon sx={{ fontSize: 40, mr: 2 }} />
                      <Box flex={1}>
                        <Typography variant="subtitle2">{uploadedFile.name}</Typography>
                        <Typography variant="caption" color="text.secondary">Comprobante cargado</Typography>
                      </Box>
                      <IconButton color="error" onClick={handleRemoveFile}>
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Paper>
                ) : paymentFile ? (
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Box display="flex" alignItems="center">
                      {filePreview ? (
                        <Box
                          component="img"
                          src={filePreview}
                          sx={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 1, mr: 2 }}
                        />
                      ) : (
                        <CloudUploadIcon sx={{ fontSize: 40, mr: 2 }} />
                      )}
                      <Box flex={1}>
                        <Typography variant="subtitle2">{paymentFile.name}</Typography>
                        <Typography variant="caption">{(paymentFile.size / 1024).toFixed(1)} KB</Typography>
                      </Box>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleFileUpload(paymentFile)}
                        disabled={uploadingFile}
                        sx={{ mr: 1 }}
                      >
                        {uploadingFile ? <CircularProgress size={20} /> : 'Subir'}
                      </Button>
                      <IconButton size="small" color="error" onClick={handleRemoveFile}>
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Paper>
                ) : (
                  <Paper
                    sx={{
                      border: '2px dashed #ccc',
                      borderRadius: 2,
                      p: 3,
                      textAlign: 'center',
                      cursor: 'pointer',
                      '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.50' }
                    }}
                    onClick={() => document.getElementById('receipt-upload')?.click()}
                  >
                    <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                    <Typography>Haz clic para subir un comprobante</Typography>
                    <Typography variant="caption">Formatos: JPG, PNG, PDF (máx. 5MB)</Typography>
                  </Paper>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Botones */}
          <Grid size={{ xs: 12 }}>
            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button variant="outlined" onClick={() => navigate('/additional-payments')} disabled={loading}>
                Cancelar
              </Button>
              <Button type="submit" variant="contained" startIcon={<SaveIcon />} disabled={loading || success}>
                {loading ? <CircularProgress size={24} /> : 'Registrar Pago'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default AdditionalPaymentForm;